import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { createPageUrl } from '@/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Building2, FileText, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

export default function CompanyOnboarding() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [companyData, setCompanyData] = useState({
    company_name: '',
    industry: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    hr_contact_email: '',
    employer_wait_period_days: 60,
    organization_code: generateOrgCode()
  });

  useEffect(() => {
    const savedData = sessionStorage.getItem('companyRegData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setCompanyData(prev => ({
          ...prev,
          company_name: parsed.companyName || '',
          industry: parsed.industry || '',
          phone: parsed.phone || '',
          hr_contact_email: parsed.adminEmail || ''
        }));
        sessionStorage.removeItem('companyRegData');
      } catch (err) {
        console.error('Failed to parse saved data');
      }
    }
  }, []);

  function generateOrgCode() {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  }

  const progress = (step / 4) * 100;

  const handleNext = () => {
    if (step === 1 && !companyData.company_name) {
      toast.error('Company name is required');
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      const user = await api.auth.me();
      
      // Create employer
      const employer = await api.asServiceRole.entities.Employer.create({
        ...companyData,
        hr_contact_email: user.email
      });

      // Update user with employer_id and role
      await api.auth.updateMe({
        user_role: 'employer_hr',
        employer_id: employer.id
      });

      toast.success('Setup complete! Welcome to BenefitsPro');
      window.location.href = createPageUrl('EmployerDashboard');
    } catch (err) {
      toast.error('Failed to complete setup');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">Welcome to BenefitsPro!</h1>
          <p className="text-slate-600">Let's get your benefits program set up in just a few steps</p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">Step {step} of 4</span>
            <span className="text-sm font-medium text-slate-600">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Company Info */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="w-6 h-6 text-purple-500" />
                    <CardTitle>Company Information</CardTitle>
                  </div>
                  <p className="text-sm text-slate-500">Tell us about your organization</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Company Name *</label>
                    <Input
                      value={companyData.company_name}
                      onChange={(e) => setCompanyData({...companyData, company_name: e.target.value})}
                      placeholder="Acme Corporation"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Industry</label>
                    <Input
                      value={companyData.industry}
                      onChange={(e) => setCompanyData({...companyData, industry: e.target.value})}
                      placeholder="Technology, Healthcare, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">HR Contact Email</label>
                    <Input
                      type="email"
                      value={companyData.hr_contact_email}
                      onChange={(e) => setCompanyData({...companyData, hr_contact_email: e.target.value})}
                      placeholder="hr@company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <Input
                      value={companyData.phone}
                      onChange={(e) => setCompanyData({...companyData, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <Button onClick={handleNext} className="w-full bg-gradient-to-r from-purple-500 to-pink-500">
                    Continue
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Step 2: Address */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="w-6 h-6 text-purple-500" />
                    <CardTitle>Company Address</CardTitle>
                  </div>
                  <p className="text-sm text-slate-500">Where is your company located?</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Street Address</label>
                    <Input
                      value={companyData.address}
                      onChange={(e) => setCompanyData({...companyData, address: e.target.value})}
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">City</label>
                      <Input
                        value={companyData.city}
                        onChange={(e) => setCompanyData({...companyData, city: e.target.value})}
                        placeholder="San Francisco"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">State</label>
                      <Input
                        value={companyData.state}
                        onChange={(e) => setCompanyData({...companyData, state: e.target.value})}
                        placeholder="CA"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">ZIP Code</label>
                    <Input
                      value={companyData.zip_code}
                      onChange={(e) => setCompanyData({...companyData, zip_code: e.target.value})}
                      placeholder="94105"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={handleBack} className="flex-1">
                      Back
                    </Button>
                    <Button onClick={handleNext} className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500">
                      Continue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Step 3: Policies */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <FileText className="w-6 h-6 text-purple-500" />
                    <CardTitle>Benefit Policies</CardTitle>
                  </div>
                  <p className="text-sm text-slate-500">Configure your company's benefit rules</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Wait Period for New Hires (days)
                    </label>
                    <Input
                      type="number"
                      value={companyData.employer_wait_period_days}
                      onChange={(e) => setCompanyData({...companyData, employer_wait_period_days: parseInt(e.target.value)})}
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      How many days must new employees wait before enrolling in benefits?
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Your Organization Code:</strong> {companyData.organization_code}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      Share this code with employees so they can join your benefits program
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={handleBack} className="flex-1">
                      Back
                    </Button>
                    <Button onClick={handleNext} className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500">
                      Continue
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Step 4: Review & Finish */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                    <CardTitle>Review & Finish</CardTitle>
                  </div>
                  <p className="text-sm text-slate-500">Confirm your setup details</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-slate-600">Company Name</p>
                      <p className="text-lg font-semibold">{companyData.company_name}</p>
                    </div>

                    {companyData.industry && (
                      <div>
                        <p className="text-sm font-medium text-slate-600">Industry</p>
                        <p className="text-lg">{companyData.industry}</p>
                      </div>
                    )}

                    {companyData.address && (
                      <div>
                        <p className="text-sm font-medium text-slate-600">Address</p>
                        <p className="text-lg">
                          {companyData.address}, {companyData.city}, {companyData.state} {companyData.zip_code}
                        </p>
                      </div>
                    )}

                    <div>
                      <p className="text-sm font-medium text-slate-600">Organization Code</p>
                      <p className="text-lg font-mono font-bold text-purple-600">{companyData.organization_code}</p>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-800 font-medium mb-2">ðŸŽ‰ You're all set!</p>
                    <p className="text-sm text-green-700">
                      Click finish to complete your setup and access your dashboard.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button variant="outline" onClick={handleBack} className="flex-1">
                      Back
                    </Button>
                    <Button 
                      onClick={handleFinish} 
                      disabled={loading}
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                    >
                      {loading ? 'Setting up...' : 'Finish Setup'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}